'use client';

import { useCatalogContext } from '@dealerportal/catalog-ui/Contexts/CatalogContext';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import { NotificationCounter } from '@dealerportal/ui-components/NotificationCounter';
import { getTagType, toKebab } from '@dealerportal/utilities';
import classNames from 'classnames';
import Link from 'next/link';
import { useMemo } from 'react';

const ProductTag = ({
  tagName,
  isOpen = false,
  useCiC = false,
}: {
  tagName: string;
  isOpen: boolean;
  useCiC?: boolean;
}) => {
  const { type, label, slug } = useMemo(
    () => getTagType(tagName, useCiC),
    [tagName, useCiC]
  );

  let tagColor = 'ui-bg-blue-600 ui-border-blue-400';

  switch (type) {
    case 'price':
      tagColor = 'ui-bg-green-600 ui-border-green-400';
      break;
    case 'special':
      tagColor = 'ui-bg-red-600 ui-border-red-400';
      break;
  }

  return (
    <Link prefetch={false} className="ui-inline-block" href={`/p?tags=${slug}`}>
      <div
        id={toKebab(label)}
        data-tag-label={label}
        className={`ui-h-7 ui-flex ui-justify-center ui-items-center ui-px-3 ui-text-xs !ui-font-semibold ui-font-[inherit] ui-rounded-full ui-text-white ui-select-none ui-border ui-border-solid ${tagColor}`}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(isOpen
            ? {}
            : {
                transform: 'scale(0)',
              }),
        }}
      >
        {label}
      </div>
    </Link>
  );
};

export const ProductTags = ({
  isOpen = false,
  tags = [],
  onTagClick,
}: {
  tags: string[];
  isOpen: boolean;
  onTagClick?: () => void;
}) => {
  const { useCiC } = useCatalogContext();
  return (
    <>
      {tags.length > 2 && onTagClick && (
        <div className="ui-relative">
          <BetterButton
            btnStyle="simple"
            attributes={{
              onClick: onTagClick,
            }}
            icon={
              !isOpen
                ? `<svg xmlns="http://www.w3.org/2000/svg" class="ui-fill-gray-600" viewBox="0 0 512 512"><path d="M345 39.1L472.8 168.4c52.4 53 52.4 138.2 0 191.2L360.8 472.9c-9.3 9.4-24.5 9.5-33.9 .2s-9.5-24.5-.2-33.9L438.6 325.9c33.9-34.3 33.9-89.4 0-123.7L310.9 72.9c-9.3-9.4-9.2-24.6 .2-33.9s24.6-9.2 33.9 .2zM0 229.5L0 80C0 53.5 21.5 32 48 32l149.5 0c17 0 33.3 6.7 45.3 18.7l168 168c25 25 25 65.5 0 90.5L277.3 442.7c-25 25-65.5 25-90.5 0l-168-168C6.7 262.7 0 246.5 0 229.5zM144 144a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/></svg>`
                : '<svg xmlns="http://www.w3.org/2000/svg" class="ui-fill-gray-600" viewBox="0 0 384 512"><path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"/></svg>'
            }
            iconSize="4"
            iconClassName={`ui-transition-transform ui-duration-300 ${!isOpen ? 'ui-rotate-0' : 'ui-rotate-90'}`}
            className="ui-w-10 ui-h-10 ui-rounded-full ui-shadow-md ui-flex ui-items-center ui-justify-center ui-transition-transform ui-duration-300 ui-transform ui-rotate-0"
            replaceClass={{
              '!ui-bg-transparent': '!ui-bg-white',
              '!ui-border-0': 'ui-border ui-border-solid ui-border-gray-600',
            }}
          />
          <div className="ui-absolute -ui-right-1 -ui-top-1">
            <NotificationCounter
              count={tags.length}
              max={9}
              showPip={!isOpen}
            />
          </div>
        </div>
      )}
      <div
        className={classNames('ui-flex ui-flex-wrap ui-flex-1 ui-gap-1')}
        style={{
          transition:
            'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: 0,
          visibility: !isOpen && tags.length > 2 ? 'hidden' : 'visible',
          transform: 'scale(0.8)',
          transformOrigin: 'left center',
          ...(isOpen || tags.length <= 2
            ? { opacity: 1, transform: 'scale(1)' }
            : {}),
        }}
      >
        {tags.map((tag, index) => (
          <ProductTag
            key={index}
            tagName={tag === 'sale' ? 'On_Sale' : tag}
            isOpen={isOpen || tags.length <= 2}
            useCiC={useCiC}
          />
        ))}
      </div>
    </>
  );
};
